import React from 'react'
import { Controller } from 'react-hook-form'
import ReactSelect from 'react-select'

import {
  DropdownIndicator,
  MultiValueRemove,
  ClearIndicator,
  Option,
  Control,
} from './components'
import { customStyles as selectStyles } from './constants'
import { getSelectedValue, onChangeHandlerWrapper } from './helpers'

import { FormItem } from '../FormItem'

import type { BaseOptionType, OnChangeHandlerType, SelectProps } from './types'
import type { ReactElement } from 'react'
import type { ActionMeta } from 'react-select'

/**
 * Select component based on {@link https://react-select.com/ react-select}
 * @component
 * @param {object} error - error object {type: 'required', message: 'field is required'}
 * @param {string} label - text label above select
 * @param {Array} options - array of select options [{value: value, label:label}]
 * @param {object} rules - validation rules from {@link https://react-hook-form.com/docs/useform/register#registerRef react-hook-form}
 * @param {boolean} isMulti - allow to choose multiple values
 * @param control - control from react-hook-form
 * @example
 * import { useForm } from 'react-hook-form'
 * const { control } = useForm()
 *
 * return (
 *   <Select control={control} options={options} label="label" placeholder="Choose..."/>
 * )
 */
export const Select = <
  TFormFields extends unknown,
  ValueType,
  IsMulti extends boolean = false,
  ExtraProps = {},
>({
  control,
  options,
  label,
  name,
  rules,
  tooltip,
  error,
  defaultValue,
  onChange,
  shouldKeepLabelSpacing,
  hint,
  disabled,
  hideSelectedOptions = false,
  isMulti,
  closeMenuOnSelect = !isMulti,
  extraComponents,
  isFullWidth,
  styles = selectStyles,
  customStyles,
  value,
  'data-form-item-testid': dataTestId,
  ...restProps
}: SelectProps<TFormFields, ValueType, IsMulti, ExtraProps>): ReactElement => {
  const renderBaseSelect = ({ field }) => {
    const onChangeHandler: OnChangeHandlerType<ValueType> = (
      value: ValueType,
      actionMeta?: ActionMeta<BaseOptionType<ValueType>>
    ) => {
      field.onChange(value)
      onChange?.(value, actionMeta)
    }

    return (
      <ReactSelect<BaseOptionType<ValueType>, boolean>
        {...field}
        {...restProps}
        hideSelectedOptions={hideSelectedOptions}
        closeMenuOnSelect={closeMenuOnSelect}
        isDisabled={disabled}
        components={{
          DropdownIndicator,
          MultiValueRemove,
          ClearIndicator,
          Control,
          Option,
          ...extraComponents,
        }}
        isMulti={isMulti}
        error={error}
        menuShouldScrollIntoView={false}
        options={options}
        onChange={onChangeHandlerWrapper<ValueType>(onChangeHandler)}
        styles={customStyles ? customStyles(styles) : styles}
        value={value || getSelectedValue<ValueType>(options, field?.value)}
      />
    )
  }
  return (
    <FormItem
      label={label}
      error={error}
      tooltip={tooltip}
      hint={hint}
      isFullWidth={isFullWidth}
      shouldKeepLabelSpacing={shouldKeepLabelSpacing}
      data-testid={dataTestId}
    >
      <Controller
        control={control}
        name={name}
        rules={rules}
        defaultValue={defaultValue}
        render={renderBaseSelect}
      />
    </FormItem>
  )
}
